import type { FC } from "react";

import { MCP_READ_SCOPE } from "@arti/schemas/mcpScopes";

import { Alert } from "../common/Alert";
import {
  DASHBOARD_MCP_SCOPE_CATEGORY_COPY,
  DASHBOARD_MCP_SCOPE_CATEGORY_ORDER,
  type DashboardMcpAccessProfileTone,
  type DashboardMcpScopeOption,
  type DashboardMcpScopeRisk,
  getDashboardMcpAccessProfile,
  getDashboardMcpOptionsForCategory,
  normalizeDashboardMcpTokenScopes,
} from "./mcpScopeCatalog";

interface McpTokenScopePickerProps {
  onChange: (scopes: string[]) => void;
  selectedScopes: string[];
}

const RISK_LABELS: Record<DashboardMcpScopeRisk, string> = {
  force: "Force",
  read: "Read",
  write: "Guarded",
};

const RISK_STYLES: Record<DashboardMcpScopeRisk, string> = {
  force:
    "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200",
  read: "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200",
  write:
    "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200",
};

const PROFILE_STYLES: Record<DashboardMcpAccessProfileTone, string> = {
  baseline:
    "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200",
  elevated:
    "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100",
  recovery:
    "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100",
};

function ScopeChip({
  children,
  tone = "neutral",
}: {
  children: string;
  tone?: "accent" | "neutral";
}) {
  return (
    <span
      className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${
        tone === "accent"
          ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
          : "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      {children}
    </span>
  );
}

function ScopeCard({
  checked,
  onChange,
  option,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  option: DashboardMcpScopeOption;
}) {
  const toneStyles = checked
    ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-slate-50 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.16)] dark:border-emerald-500/40 dark:from-emerald-500/10 dark:via-slate-950/40 dark:to-slate-900/70"
    : "border-slate-200 bg-white/90 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:hover:border-slate-700 dark:hover:bg-slate-900/60";

  return (
    <label className="relative block cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-emerald-500/60">
      {/* Overlay the real checkbox ON the card (invisible, full-size) instead
          of `sr-only`. sr-only positions the input off-screen (absolute, 1px),
          so clicking the card focuses an off-screen element and the browser
          scroll-into-view jumps the whole window. Positioned over the card,
          focus-scroll is a no-op while the input stays clickable + accessible. */}
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0"
      />
      <div className={`h-full border p-3 transition-colors ${toneStyles}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-slate-900 dark:text-slate-100">
              {option.label}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              {option.description}
            </p>
          </div>
          <div
            className={`flex h-5 w-5 shrink-0 items-center justify-center border text-[10px] font-bold ${
              checked
                ? "border-emerald-500 bg-emerald-500 text-white"
                : "border-slate-300 bg-white text-transparent dark:border-slate-600 dark:bg-slate-900"
            }`}
            aria-hidden="true"
          >
            {checked ? "✓" : "·"}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span
            className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${RISK_STYLES[option.risk]}`}
          >
            {RISK_LABELS[option.risk]}
          </span>
          <ScopeChip>{option.scope}</ScopeChip>
        </div>
      </div>
    </label>
  );
}

function ScopeSection({
  onChange,
  options,
  selectedScopes,
  title,
  description,
}: {
  description: string;
  onChange: (scope: string, checked: boolean) => void;
  options: readonly DashboardMcpScopeOption[];
  selectedScopes: string[];
  title: string;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-1 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-slate-700 dark:text-slate-200">
            {title}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            {description}
          </p>
        </div>
        <ScopeChip tone="accent">
          {`${options.filter((option) => selectedScopes.includes(option.scope)).length} selected`}
        </ScopeChip>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {options.map((option) => (
          <ScopeCard
            key={option.scope}
            checked={selectedScopes.includes(option.scope)}
            onChange={(checked) => onChange(option.scope, checked)}
            option={option}
          />
        ))}
      </div>
    </section>
  );
}

export const McpTokenScopePicker: FC<McpTokenScopePickerProps> = ({
  onChange,
  selectedScopes,
}) => {
  const effectiveScopes = normalizeDashboardMcpTokenScopes(selectedScopes);
  const accessProfile = getDashboardMcpAccessProfile(selectedScopes);
  const optionalScopeCount = selectedScopes.length;

  const handleScopeChange = (scope: string, checked: boolean) => {
    const nextScopes = checked
      ? [...selectedScopes, scope]
      : selectedScopes.filter((selectedScope) => selectedScope !== scope);
    onChange(normalizeDashboardMcpTokenScopes(nextScopes));
  };

  return (
    <div className="space-y-5 border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)]">
        <div className="space-y-2">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-slate-700 dark:text-slate-200">
            Token Scopes
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Build the minimum access profile the MCP client needs, then add
            indexed knowledge or write scopes only where a human operator
            expects active participation.
          </p>
          <Alert variant="info" title="Workspace identity is always included">
            Dashboard-issued tokens always include <code>{MCP_READ_SCOPE}</code>{" "}
            so MCP clients can resolve the authenticated user and organization.
          </Alert>
        </div>

        <div className="border border-slate-300 bg-white/90 p-3 dark:border-slate-700 dark:bg-slate-950/60">
          <div className="flex flex-wrap items-center gap-2">
            <ScopeChip tone="accent">Identity required</ScopeChip>
            <ScopeChip>
              {optionalScopeCount === 0
                ? "No optional scopes"
                : `${optionalScopeCount} optional`}
            </ScopeChip>
            <span
              className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${PROFILE_STYLES[accessProfile.tone]}`}
            >
              {accessProfile.label}
            </span>
          </div>
          <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">
            {accessProfile.description}
          </p>
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-3 dark:border-emerald-500/30 dark:from-emerald-500/10 dark:via-slate-950/30 dark:to-slate-900/70">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-slate-900 dark:text-slate-100">
                Read workspace identity
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Verifies the authenticated user and organization context inside
                Arti. This baseline scope cannot be removed from dashboard
                tokens.
              </p>
            </div>
            <span className="border border-emerald-300 bg-emerald-500 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-white dark:border-emerald-500/40">
              Required
            </span>
          </div>
          <div className="mt-3">
            <ScopeChip>{MCP_READ_SCOPE}</ScopeChip>
          </div>
        </div>

        <div className="border border-slate-300 bg-white/90 p-3 dark:border-slate-700 dark:bg-slate-950/60">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-slate-700 dark:text-slate-200">
            Effective Scopes
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            The token will be issued with this exact scope set.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {effectiveScopes.map((scope) => (
              <ScopeChip key={scope}>{scope}</ScopeChip>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {DASHBOARD_MCP_SCOPE_CATEGORY_ORDER.map((category) => {
          const categoryCopy = DASHBOARD_MCP_SCOPE_CATEGORY_COPY[category];
          return (
            <ScopeSection
              key={category}
              description={categoryCopy.description}
              onChange={handleScopeChange}
              options={getDashboardMcpOptionsForCategory(category)}
              selectedScopes={selectedScopes}
              title={categoryCopy.title}
            />
          );
        })}
      </div>
    </div>
  );
};
